<script setup lang="ts">
import { format } from "date-fns";
import Simplebar from "simplebar-vue";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import type { EstimatorReportComparisonDto } from "@/dto";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useModalsStore } from "@/stores/modals";

const api = useApi();
const route = useRoute();
// const router = useRouter();
const modals = useModalsStore();
const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);
const isLoading = ref(true);
const comparisons = ref<EstimatorReportComparisonDto[]>([]);

async function fetchComparisons() {
  isLoading.value = true;
  comparisons.value = await api.listComparisons(tenantName.value, projectName.value);
  isLoading.value = false;
}

function formatDatetime(dt: string) {
  return format(new Date(dt), "d MMMM yyyy");
}

async function deleteComparison(id: number) {
  const comparison = comparisons.value.find((c) => c.id === id);
  if (comparison) {
    const confirmed = await modals.confirm({
      title: "Delete comparison",
      message: `Do you really want to delete the comparison "${comparison.name}" ?`,
    });
    if (confirmed) {
      await api.deleteComparison(tenantName.value, projectName.value, id);
      await fetchComparisons();
    }
  }
}

function getVolumeClass(comparison: EstimatorReportComparisonDto) {
  if (comparison.compared_reports.length < 3) {
    return "low";
  } else if (comparison.compared_reports.length < 9) {
    return "normal";
  } else if (comparison.compared_reports.length < 13) {
    return "high";
  } else {
    return "very-high";
  }
}

onMounted(async () => {
  await fetchComparisons();
});
</script>

<template>
  <div class="comparison-list">
    <Transition name="fade">
      <div v-if="isLoading && comparisons.length === 0" class="loader">
        <SkoreSpinner />
      </div>
      <div v-else-if="comparisons.length === 0" class="placeholder">
        <div class="label-l">No comparisons</div>
        <div class="label-s">You may create comparisons in the experiment tab.</div>
      </div>
      <Simplebar v-else class="scroller">
        <div class="comparisons">
          <TransitionGroup name="list">
            <div v-for="c in comparisons" :key="c.id" class="comparison">
              <div class="header">
                <div class="name label-s"><i class="icon icon-cube" /> {{ c.name }}</div>
                <div class="reports-count paragraph-s">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="icon-volume"
                    :class="getVolumeClass(c)"
                  >
                    <path
                      d="M0 11.5C0 11.2239 0.223858 11 0.5 11H2.5C2.77614 11 3 11.2239 3 11.5V15H0V11.5Z"
                      class="bar1"
                    />
                    <path
                      d="M4 8.5C4 8.22386 4.22386 8 4.5 8H6.5C6.77614 8 7 8.22386 7 8.5V15H4V8.5Z"
                      class="bar2"
                    />
                    <path
                      d="M8 3.5C8 3.22386 8.22386 3 8.5 3H10.5C10.7761 3 11 3.22386 11 3.5V15H8V3.5Z"
                      class="bar3"
                    />
                    <path
                      d="M12 0.5C12 0.223858 12.2239 0 12.5 0H14.5C14.7761 0 15 0.223858 15 0.5V15H12V0.5Z"
                      class="bar4"
                    />
                  </svg>
                  {{ c.compared_reports.length }} models compared
                  <span v-if="getVolumeClass(c) != 'normal'">
                    ({{ getVolumeClass(c).replace("-", " ") }} volume)
                  </span>
                </div>
              </div>
              <div class="content">
                <div class="sections">
                  <div class="section">
                    <div class="value label-s">{{ c.best_trade_offs.length }} models selected</div>
                    <div class="name paragraph-s">
                      <i class="icon icon-selected-cube" /> Best trade offs
                    </div>
                  </div>
                  <div class="section">
                    <div class="value label-s">{{ formatDatetime(c.updated_at) }}</div>
                    <div class="name paragraph-s">
                      <i class="icon icon-calendar" /> Date of last comparison run
                    </div>
                  </div>
                </div>
                <div class="actions">
                  <RouterLink
                    class="skore-button tertiary link"
                    :to="{
                      name: RouteNames.Comparison,
                      params: { tenantName, projectName, comparisonId: c.id },
                    }"
                  >
                    <i class="icon icon-arrow-right" />
                  </RouterLink>
                  <DropdownButton icon-left="icon-more-vertical" kind="tertiary">
                    <DropdownButtonItem
                      icon-left="icon-trash"
                      label="Delete"
                      @click="deleteComparison(c.id)"
                    />
                  </DropdownButton>
                </div>
              </div>
            </div>
          </TransitionGroup>
        </div>
      </Simplebar>
    </Transition>
  </div>
</template>

<style scoped>
.comparison-list {
  height: 100%;

  .loader {
    display: flex;
    height: 100%;
    justify-content: center;
  }

  .scroller {
    height: 100%;
  }

  .placeholder {
    display: flex;
    height: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .label-s {
      color: var(--color-text-secondary);
    }
  }

  .comparisons {
    display: flex;
    max-height: 100%;
    flex-direction: column;
    gap: var(--spacing-24);

    .comparison {
      border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
      border-radius: var(--radius-s);

      .header {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: var(--spacing-12) var(--spacing-16);
        border-bottom: solid var(--stroke-width-m) var(--color-stroke-background-primary);
        gap: var(--spacing-24);

        .name {
          padding: 0 var(--spacing-6);
          border: solid var(--stroke-width-m) var(--color-stroke-background-branding-secondary);
          border-radius: var(--radius-xs);
          color: var(--color-text-tertiary);

          .icon {
            margin-right: var(--spacing-4);
            vertical-align: middle;
          }
        }

        .icon-volume {
          margin-right: var(--spacing-4);
          vertical-align: text-bottom;

          &.low {
            .bar1 {
              fill: #f0bd30;
            }

            .bar2,
            .bar3,
            .bar4 {
              fill: #e6e6e6;
            }
          }

          &.normal {
            .bar1,
            .bar2 {
              fill: #f0bd30;
            }

            .bar3,
            .bar4 {
              fill: #e6e6e6;
            }
          }

          &.high {
            .bar1,
            .bar2,
            .bar3 {
              fill: #f0bd30;
            }

            .bar4 {
              fill: #e6e6e6;
            }
          }

          &.very-high {
            .bar1,
            .bar2,
            .bar3,
            .bar4 {
              fill: #f0bd30;
            }
          }
        }
      }

      .content {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-16);

        .sections {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: var(--spacing-24);

          .section {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-8);

            .name {
              color: var(--color-text-secondary);

              .icon {
                vertical-align: middle;
              }
            }
          }
        }

        .actions {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: var(--spacing-8);

          .link {
            i {
              transition: transform var(--animation-duration) var(--animation-easing);
            }

            &:hover {
              i {
                transform: translateX(2px);
              }
            }
          }
        }
      }
    }
  }
}
</style>
